/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { AdminProgramService } from "app/services/admin-program.service";
import { FetchData } from "app/shared/generalMethods";
import { InputValidation } from "app/shared/inputValidation";

import { AdminProfileService } from "app/services/admin-profile.service";

@Component({
  selector: "app-admin-profile",
  templateUrl: "./admin-profile.component.html",
  styleUrls: ["./admin-profile.component.scss"],
})
export class AdminProfileComponent implements OnInit {
  isEditMode = false;
  pageLoader = false;
  pageLoaderText = "Loading...";
  adminForm: FormGroup;
  apiResult: any;
  adminData: any;
  roleOptions: { key: string; value: string }[] = []; // For lstRolePicklist

  constructor(
    private _formBuilder: FormBuilder,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _fetchData: FetchData,
    private _AdminProfileService: AdminProfileService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadAdminData();
  }

  initializeForm(): void {
    this.adminForm = this._formBuilder.group({
      administratorName: ["", Validators.required],
      email: [
        { value: "", disabled: this.isEditMode },
        [Validators.required, Validators.email],
      ],
      phone: ["", Validators.required],
      role: [{ value: "", disabled: this.isEditMode }, Validators.required],
      city: [""],
    });
  }

  loadAdminData(): void {
    this.pageLoader = true;

    // Assuming _fetchData is coming from a service or a resolver
    const data = this._fetchData.data;

    if (data?.lstAdministratorData?.[0]) {
      this.adminData = data.lstAdministratorData[0];

      this.roleOptions = data.lstRolePicklist ?? [
        { key: "Admin", value: "Administrator" },
        { key: "Staff", value: "Staff" },
      ];

      this.adminForm.patchValue({
        administratorName: this.adminData.Administrator_Name__c,
        email: this.adminData.Email__c,
        phone: this.adminData.Phone__c,
        role: this.adminData.Role__c,
        city: this.adminData.Town_City__c,
      });
    }

    this.pageLoader = false;
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    if (!this.isEditMode) {
      // Reset form when canceling edit
      this.initializeForm();
      this.adminForm.patchValue({
        administratorName: this.adminData.Administrator_Name__c,
        email: this.adminData.Email__c,
        phone: this.adminData.Phone__c,
        role: this.adminData.Role__c,
        city: this.adminData.Town_City__c,
      });
    }
  }

  onFormSubmit(): void {
    this.pageLoader = true;
    this.pageLoaderText = "Updating admin profile..."; // Or use your loading message from lstAppSettings

    // Re-enable disabled fields to include them in submission
    if (this.adminForm.get("email").disabled) {
      this.adminForm.get("email").enable();
    }
    if (this.adminForm.get("role").disabled) {
      this.adminForm.get("role").enable();
    }

    const adminId = localStorage.getItem("adminId");

    this.pageLoader = true;
    this.pageLoaderText = "Updating admin profile...";

    const updatedAdminData = {
      Administrator_Name__c: this.adminForm.value.administratorName,
      Email__c: this.adminForm.value.email,
      Phone__c: this.adminForm.value.phone,
      Role__c: this.adminForm.value.role,
      Town_City__c: this.adminForm.value.city,
      Is_Active__c: true,
    };

    this._AdminProfileService
      .updateAdminProfile(updatedAdminData, adminId)
      .subscribe(
        (response) => {
          if (response && response.success) {
            this._fetchData.setLocalStorage(response.msg);
            this._snackBar.open(
              "Admin profile updated successfully!",
              "Close",
              {
                duration: 3000,
              }
            );

            this.isEditMode = false;
            this.pageLoader = false;

            this._router
              .navigateByUrl("/", { skipLocationChange: true })
              .then(() => {
                this._router.navigate(["/profile-detail"]);
              });
          } else {
            this.pageLoader = false;
            this._snackBar.open("Failed to update admin profile!", "Close", {
              duration: 3000,
            });
          }
        },
        (error) => {
          console.error("Update Admin Error:", error);
          this.pageLoader = false;
          this._snackBar.open("Error updating admin profile!", "Close", {
            duration: 3000,
          });
        }
      );
  }

  onInputAlphabet(event: any): void {
    const pattern = /^[A-Za-z\s]*$/;
    const input = event.target.value;
    if (!pattern.test(input)) {
      event.target.value = input.replace(/[^A-Za-z\s]/g, "");
      this.adminForm
        .get(event.target.getAttribute("formControlName"))
        ?.setValue(event.target.value);
    }
  }

  onInputNumeric(event: any): void {
    const pattern = /^[0-9]*$/;
    const input = event.target.value;
    if (!pattern.test(input)) {
      event.target.value = input.replace(/[^0-9]/g, "");
      this.adminForm
        .get(event.target.getAttribute("formControlName"))
        ?.setValue(event.target.value);
    }
  }

  // Helper method to get role display value
  getRoleDisplayValue(roleKey: string): string {
    const role = this.roleOptions.find((r) => r.key === roleKey);
    return role ? role.value : roleKey;
  }
}
